import { useSuspenseQuery } from "@apollo/client/react";
import { AlertTriangle, Building2, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui";
import {
  GetOrganizationsForUserSelectDocument,
  type GetOrganizationsForUserSelectQuery,
} from "~/features/admin/users/gqls";
import { useUserOrganizationUpdate } from "~/features/admin/users/hooks";
import type { UserRow } from "~/features/admin/users/types";

interface UserOrganizationChangeDialogProps {
  user: UserRow;
  onOpenChange: (open: boolean) => void;
}

export function UserOrganizationChangeDialog({ user, onOpenChange }: UserOrganizationChangeDialogProps) {
  const { t } = useTranslation();
  const { data } = useSuspenseQuery<GetOrganizationsForUserSelectQuery>(GetOrganizationsForUserSelectDocument);
  const { submit, reset, processing, outcome } = useUserOrganizationUpdate();
  const [destinationShortCode, setDestinationShortCode] = useState("");

  const destinations = useMemo(
    () => data.organizations.filter((organization) => organization.shortCode !== user.organization.shortCode),
    [data.organizations, user.organization.shortCode],
  );
  const destination = destinations.find((organization) => organization.shortCode === destinationShortCode);
  const requiresConfirmation = outcome.status === "requires-confirmation";

  useEffect(() => {
    if (outcome.status !== "success" || !destination) return;
    toast.success(t("feature.users.organizationChange.success", { organization: destination.name }));
    onOpenChange(false);
  }, [destination, onOpenChange, outcome.status, t]);

  const handleOpenChange = (open: boolean) => {
    if (!open && processing) return;
    if (!open) reset();
    onOpenChange(open);
  };

  const handleSubmit = async (removeMembershipsOutsideDestination: boolean) => {
    if (!destinationShortCode) return;
    await submit(
      {
        userId: user.id,
        organizationShortCode: destinationShortCode,
      },
      removeMembershipsOutsideDestination,
    );
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("feature.users.organizationChange.title")}</DialogTitle>
          <DialogDescription>
            {t("feature.users.organizationChange.description", {
              user: user.displayName || user.email,
            })}
          </DialogDescription>
        </DialogHeader>

        {requiresConfirmation ? (
          <>
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-[var(--radius-md)] border border-warning bg-warning-bg p-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
                <div className="space-y-1">
                  <h3 className="text-14-bold text-fg-1">{t("feature.users.organizationChange.confirmationTitle")}</h3>
                  <p className="text-14-regular text-fg-2">
                    {t("feature.users.organizationChange.confirmationDescription")}
                  </p>
                </div>
              </div>

              <div className="space-y-1.5">
                <p className="text-12-medium text-fg-2">{t("feature.users.organizationChange.serverMessage")}</p>
                <p className="whitespace-pre-wrap rounded-[var(--radius-md)] bg-bg-subtle p-3 font-mono text-12-regular text-fg-1">
                  {outcome.message}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={reset} disabled={processing}>
                {t("feature.users.organizationChange.back")}
              </Button>
              <Button type="button" onClick={() => void handleSubmit(true)} disabled={processing}>
                {processing && <Loader2 className="animate-spin" />}
                {t("feature.users.organizationChange.confirm")}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-4">
              <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 rounded-[var(--radius-md)] border border-border-default bg-bg-subtle p-3">
                <OrganizationSummary
                  label={t("feature.users.organizationChange.currentOrganization")}
                  name={user.organization.name}
                  shortCode={user.organization.shortCode}
                />
                <span aria-hidden className="text-fg-3">
                  →
                </span>
                <OrganizationSummary
                  label={t("feature.users.organizationChange.destinationOrganization")}
                  name={destination?.name ?? "—"}
                  shortCode={destination?.shortCode ?? ""}
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="user-organization-destination" className="text-12-medium text-fg-2">
                  {t("feature.users.organizationChange.destinationOrganization")}
                </label>
                <Select
                  value={destinationShortCode}
                  onValueChange={setDestinationShortCode}
                  disabled={processing || destinations.length === 0}
                >
                  <SelectTrigger id="user-organization-destination">
                    <SelectValue placeholder={t("feature.users.organizationChange.selectDestination")} />
                  </SelectTrigger>
                  <SelectContent>
                    {destinations.map((organization) => (
                      <SelectItem key={organization.id} value={organization.shortCode}>
                        {organization.name} ({organization.shortCode})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {destinations.length === 0 && (
                  <p className="text-12-regular text-fg-3">{t("feature.users.organizationChange.noDestination")}</p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={processing}>
                {t("ui.cancel")}
              </Button>
              <Button
                type="button"
                onClick={() => void handleSubmit(false)}
                disabled={!destinationShortCode || processing}
              >
                {processing ? <Loader2 className="animate-spin" /> : <Building2 />}
                {processing
                  ? t("feature.users.organizationChange.processing")
                  : t("feature.users.organizationChange.submit")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OrganizationSummary({ label, name, shortCode }: { label: string; name: string; shortCode: string }) {
  return (
    <div className="min-w-0 space-y-0.5">
      <p className="text-12-medium uppercase tracking-[0.04em] text-fg-3">{label}</p>
      <p className="truncate text-14-regular font-medium text-fg-1">{name}</p>
      {shortCode && <p className="truncate font-mono text-12-regular text-fg-3">{shortCode}</p>}
    </div>
  );
}
